import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export default function ClientSuggestionsList({
  refreshKey = 0,
  compact = false,
  showSubmitLink = true,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(apiUrl("/api/client/suggestions"), {
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setRows([]);
        setError(
          clientSafeMessage(
            data.message,
            "Could not load suggestions.",
          ),
        );
        return;
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch {
      setRows([]);
      setError("Network error. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <div className={compact ? "" : "space-y-4"}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Ideas from our clients — we read every one.
        </p>
        {showSubmitLink ? (
          <Link
            to="/client/support/suggestions"
            className="text-sm font-semibold text-brand-500 hover:underline dark:text-brand-300"
          >
            Submit yours →
          </Link>
        ) : null}
      </div>

      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500">Loading suggestions…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500 dark:border-white/15 dark:text-gray-400">
          No suggestions yet. Be the first to share an idea.
        </p>
      ) : (
        <ul className={`space-y-3 ${compact ? "max-h-[320px] overflow-y-auto pr-1" : ""}`}>
          {rows.map((row) => (
            <li
              key={row.id}
              className="rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3 dark:border-white/10 dark:bg-navy-900/50"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-bold text-navy-800 dark:text-white">
                  {row.clientName || "Client"}
                </p>
                <time
                  className="text-[11px] text-gray-500 dark:text-gray-500"
                  dateTime={row.createdAt}
                >
                  {formatWhen(row.createdAt)}
                </time>
              </div>
              {row.title ? (
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-300">
                  {row.title}
                </p>
              ) : null}
              <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                {row.message}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
